import type { FC } from "react";
import { ChoiceChipGroup, type ChoiceChipOption } from "@visioni/a3s-ui/components";
import type { IsoWeekday } from "../../../client-messages";
import type { ReportEmailDeliveryFrequencyName } from "../../hooks/report-email-delivery-form";
import { reportUpdateTimeLabel, type ReportUpdateTimeContext } from "../../report-update-time";
import { ScheduleRow } from "../ScheduleRow";

export interface FrequencyFieldProps extends ReportUpdateTimeContext {
  readonly value: ReportEmailDeliveryFrequencyName;
  /** The weekly send day, so the helper can name the viewer's day when it differs. */
  readonly weekday: IsoWeekday;
  readonly onChange: (value: ReportEmailDeliveryFrequencyName) => void;
  readonly disabled?: boolean | undefined;
}

const LABEL = "How often";

const OPTIONS: readonly ChoiceChipOption<ReportEmailDeliveryFrequencyName>[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

// Every email carries the same cumulative workbook as Download (hire start to
// yesterday), so the helper describes when it's sent, not a period it covers.
// The time is the viewer's, so "every day" rather than "every morning": the run
// is evening in Europe.
function helperText(value: ReportEmailDeliveryFrequencyName, input: ReportUpdateTimeContext & { weekday: IsoWeekday }) {
  return value === "daily" ?
      `Emailed every day after the report updates at ${reportUpdateTimeLabel({ now: input.now, viewerTimeZone: input.viewerTimeZone })}.`
    : `Emailed every week on the day you choose, after the report updates at ${reportUpdateTimeLabel(input)}.`;
}

/** Daily or weekly delivery of the report email. */
export const FrequencyField: FC<FrequencyFieldProps> = ({
  value,
  onChange,
  disabled,
  now,
  viewerTimeZone,
  weekday,
}) => (
  <ScheduleRow label={LABEL} helperText={helperText(value, { now, viewerTimeZone, weekday })}>
    <ChoiceChipGroup
      options={OPTIONS}
      value={value}
      onChange={onChange}
      accessibilityLabel={LABEL}
      disabled={disabled}
    />
  </ScheduleRow>
);
